import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { CheckIcon, SpinnerIcon, XIcon } from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { EmailPersonalFolderTreeNode } from '../useData';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';

const FolderIcon = (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const ShareIcon = (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
  </svg>
);

const PencilIcon = (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const FolderMoveIcon = (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 14l4-4m0 0l4 4m-4-4v8" />
  </svg>
);

const TrashIcon = (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const FolderPlusIcon = (
  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v6m-3-3h6" />
  </svg>
);

const ChevronDownIcon = (
  <svg className="w-4 h-4 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

function collectDescendantIds(node: EmailPersonalFolderTreeNode): Set<number> {
  const set = new Set<number>([node.id]);
  if (node.children?.length) {
    node.children.forEach((c) => collectDescendantIds(c).forEach((id) => set.add(id)));
  }
  return set;
}

function flattenForMovePicker(
  nodes: EmailPersonalFolderTreeNode[],
  excludeIds: Set<number>,
  depth = 0
): { id: number; name: string; depth: number }[] {
  const out: { id: number; name: string; depth: number }[] = [];
  for (const node of nodes) {
    if (excludeIds.has(node.id)) continue;
    out.push({ id: node.id, name: node.name, depth });
    if (node.children?.length) {
      out.push(...flattenForMovePicker(node.children, excludeIds, depth + 1));
    }
  }
  return out;
}

interface PersonalFolderOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: EmailPersonalFolderTreeNode | null;
  personalFolders: EmailPersonalFolderTreeNode[];
  onShare: (folder: EmailPersonalFolderTreeNode) => void;
  onRename: (folderId: number, name: string) => Promise<void>;
  onMove: (folderId: number, parentId: number | null) => Promise<void>;
  onAddSubfolder?: (folder: EmailPersonalFolderTreeNode) => void;
  onDelete: (folderId: number) => Promise<void>;
}

const PersonalFolderOptionsModal: React.FC<PersonalFolderOptionsModalProps> = ({
  isOpen,
  onClose,
  folder,
  personalFolders,
  onShare,
  onRename,
  onMove,
  onAddSubfolder,
  onDelete,
}) => {
  const { colors } = useTheme();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [showMovePicker, setShowMovePicker] = useState(false);
  const [savingMove, setSavingMove] = useState(false);

  useEffect(() => {
    if (folder && isOpen) {
      setRenaming(false);
      setRenameValue(folder.name);
      setShowMovePicker(false);
    }
  }, [folder, isOpen]);

  const handleShare = () => {
    if (folder) onShare(folder);
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!folder) return;
    setShowDeleteConfirm(false);
    setDeleting(true);
    try {
      await onDelete(folder.id);
      onClose();
    } catch {
      /**/
    } finally {
      setDeleting(false);
    }
  };

  const startRename = () => {
    if (folder) {
      setRenameValue(folder.name);
      setRenaming(true);
    }
  };

  const saveRename = async () => {
    if (!folder || !renameValue.trim()) return;
    setSavingRename(true);
    try {
      await onRename(folder.id, renameValue.trim());
      setRenaming(false);
    } finally {
      setSavingRename(false);
    }
  };

  const cancelRename = () => {
    setRenameValue(folder?.name ?? '');
    setRenaming(false);
  };

  const moveOptions = folder
    ? flattenForMovePicker(personalFolders, collectDescendantIds(folder))
    : [];
  const handleMoveSelect = async (parentId: number | null) => {
    if (!folder) return;
    setSavingMove(true);
    try {
      await onMove(folder.id, parentId);
      setShowMovePicker(false);
      onClose();
    } finally {
      setSavingMove(false);
    }
  };

  if (!folder) return null;

  const entryCount = typeof folder.entryCount === 'number' ? folder.entryCount : 0;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalHeader>
          <ModalTitle>Folder options</ModalTitle>
          <button
            type="button"
            onClick={onClose}
            className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover)}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </ModalHeader>
        <ModalContent>
          {/* Folder identity card */}
          <div
            className={cn(
              'flex items-center gap-3 rounded-xl border p-4 mb-6',
              colors.border,
              'bg-gray-50/80 dark:bg-gray-800/50'
            )}
          >
            <div
              className={cn(
                'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg',
                'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
              )}
            >
              {FolderIcon}
            </div>
            <div className="min-w-0 flex-1">
              {renaming ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveRename();
                      if (e.key === 'Escape') cancelRename();
                    }}
                    maxLength={255}
                    autoFocus
                    className={cn(
                      'w-full px-3 py-2 rounded-lg border text-sm font-medium',
                      colors.background,
                      colors.border,
                      'focus:outline-none focus:ring-2 focus:ring-amber-500/50'
                    )}
                    aria-label="Folder name"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={cancelRename}
                      disabled={savingRename}
                      className="p-2"
                      title="Cancel"
                      aria-label="Cancel"
                    >
                      <XIcon className="w-4 h-4" />
                      <span className="sr-only">Cancel</span>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={saveRename}
                      disabled={savingRename || !renameValue.trim()}
                      className="p-2"
                      title={savingRename ? 'Saving…' : 'Save'}
                      aria-label={savingRename ? 'Saving…' : 'Save'}
                    >
                      {savingRename ? <SpinnerIcon className="w-4 h-4" /> : <CheckIcon className="w-4 h-4" />}
                      <span className="sr-only">{savingRename ? 'Saving…' : 'Save'}</span>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className={cn('font-semibold truncate', colors.text)} title={folder.name}>
                    {folder.name}
                  </p>
                  <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                    {entryCount} {entryCount === 1 ? 'email' : 'emails'}
                  </p>
                </>
              )}
            </div>
            {!renaming && (
              <Button type="button" variant="ghost" size="sm" onClick={startRename} className="flex-shrink-0">
                {PencilIcon}
                <span className="sr-only">Rename</span>
              </Button>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-1">
            <p className={cn('px-1 text-[11px] font-semibold uppercase tracking-wider mb-2', colors.textMuted)}>
              Organize
            </p>
            <div className={cn('rounded-xl border overflow-hidden', colors.border)}>
              {showMovePicker ? (
                <div className={cn('p-3 border-b', colors.border, 'bg-gray-50/50 dark:bg-gray-800/30')}>
                  <p className={cn('text-xs font-medium mb-2', colors.textMuted)}>Move folder to</p>
                  <div className="max-h-36 overflow-y-auto rounded-lg space-y-0.5">
                    <button
                      type="button"
                      onClick={() => handleMoveSelect(null)}
                      disabled={savingMove}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium',
                        colors.text,
                        'hover:bg-gray-200/80 dark:hover:bg-gray-700/80 disabled:opacity-50'
                      )}
                    >
                      {FolderIcon}
                      Top level
                    </button>
                    {moveOptions.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => handleMoveSelect(f.id)}
                        disabled={savingMove}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium',
                          colors.text,
                          'hover:bg-gray-200/80 dark:hover:bg-gray-700/80 disabled:opacity-50'
                        )}
                        style={{ paddingLeft: 12 + f.depth * 20 }}
                      >
                        {FolderIcon}
                        {f.name}
                      </button>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 p-2"
                    onClick={() => setShowMovePicker(false)}
                    title="Cancel"
                    aria-label="Cancel"
                  >
                    <XIcon className="w-4 h-4" />
                    <span className="sr-only">Cancel</span>
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowMovePicker(true)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-4 py-3 text-sm font-medium transition-colors',
                    colors.text,
                    'hover:bg-gray-100 dark:hover:bg-gray-800/80'
                  )}
                >
                  <span className="flex items-center gap-3">
                    {FolderMoveIcon}
                    Move to…
                  </span>
                  {ChevronDownIcon}
                </button>
              )}
              {onAddSubfolder && !showMovePicker && (
                <button
                  type="button"
                  onClick={() => {
                    if (folder) onAddSubfolder(folder);
                    onClose();
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-t',
                    colors.border,
                    colors.text,
                    'hover:bg-gray-100 dark:hover:bg-gray-800/80'
                  )}
                >
                  {FolderPlusIcon}
                  Add subfolder
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 space-y-1">
            <p className={cn('px-1 text-[11px] font-semibold uppercase tracking-wider mb-2', colors.textMuted)}>
              Sharing
            </p>
            <button
              type="button"
              onClick={handleShare}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition-colors',
                colors.border,
                colors.text,
                'hover:bg-gray-100 dark:hover:bg-gray-800/80'
              )}
            >
              {ShareIcon}
              Share folder
            </button>
          </div>

          <div className="mt-4 space-y-1">
            <p className={cn('px-1 text-[11px] font-semibold uppercase tracking-wider mb-2', colors.textMuted)}>
              Danger zone
            </p>
            <button
              type="button"
              onClick={handleDeleteClick}
              disabled={deleting}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition-colors',
                'border-red-200 dark:border-red-900/50',
                'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50'
              )}
            >
              {TrashIcon}
              Delete folder
            </button>
          </div>
        </ModalContent>
      </Modal>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete personal folder"
        message="Are you sure you want to delete this folder? Emails in it will not be deleted, only the folder and its link to those emails."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
};

export default PersonalFolderOptionsModal;
